import moment from 'moment';
import { inject, Injectable } from '@angular/core';
import { TimezoneService } from '@core/services/timezone.service';
import Helpers from '@core/utils/helpers';
import { Occupancy, Zone } from '@map/models';
import { BaseService } from '@shared/services/base.service';
import { Observable, map, tap, catchError, share } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ZoneService extends BaseService {
    private readonly timezoneService = inject(TimezoneService);

    constructor() {
        super();
        this.setApiName('Zones');
    }

    override mapResponseToObject<T>(response: any): T {
        const zone = new Zone(response) as any;
        return zone;
    }
    override mapArrayResponseToObject<T>(response: any): T {
        return response?.$values?.map((responseJson: any) => new Zone(responseJson));
    }

    getOccupancy(id: number, year?: number, month?: number, day?: number): Observable<Occupancy> {
        let queryParams = `${id}/Occupancy?year=${year}&month=${month}`;
        if (day) {
            queryParams += `&day=${day}`;
        }
        return this.httpClient.get(`${this.apiUrl()}/${queryParams}`).pipe(
            map((response: any) => new Occupancy(response)),
            tap((occupancyData: Occupancy) => Helpers.setTimezoneOffsets(occupancyData.occupancyStats, this.timezoneService.timezone())),
            tap((occupancyData: Occupancy) => occupancyData.occupancyStats.filter((element) => element.day !== 0)),
            tap((occupancyData: Occupancy) => occupancyData.occupancyStats.sort((a, b) => a.hour - b.hour)),
            catchError((error) => {
                return this.handleError(error);
            }),
            share()
        );
    }

    getOccupancyRange(options: {
        id: number;
        from: Date;
        to: Date;
        timeRange?: {
            startTime: string;
            endTime: string;
        };
        daily?: boolean;
        floorId?: number | null;
        setTimezoneOffsets?: boolean;
    }): Observable<Occupancy> {
        // Set default values for optional parameters
        options.daily = options.daily ?? false;
        options.setTimezoneOffsets = options.setTimezoneOffsets ?? true;
        const timezoneDifference = options.setTimezoneOffsets
            ? Helpers.getLocalToTenantTimeZoneDifference(this.timezoneService.timezone())
            : 0;
        const from = moment(options.from).subtract(timezoneDifference, 'hours').toDate();
        const to = moment(options.to).subtract(timezoneDifference, 'hours').toDate();
        // Add floorId as a query parameter if it is not null
        let queryParams = `${options.id}/Occupancy/From/${Helpers.formatDateToJSON(from)}/To/${Helpers.formatDateToJSON(to)}?daily=${
            options.daily
        }`;
        if (options.floorId) {
            queryParams += `&buildingFloorId=${options.floorId}`;
        }
        return this.httpClient.get(`${this.apiUrl()}/${queryParams}`).pipe(
            map((response: any) => (response = new Occupancy(response))),
            tap((occupancyData: Occupancy) => {
                if (options.setTimezoneOffsets) {
                    Helpers.setTimezoneOffsets(occupancyData.occupancyStats, this.timezoneService.timezone());
                }
                if (options.timeRange) {
                    const startHour = Number(options.timeRange.startTime.split(':')[0]);
                    const endHour = Number(options.timeRange.endTime.split(':')[0]);
                    occupancyData.occupancyStats = occupancyData.occupancyStats.filter((stat) =>
                        endHour <= startHour ? stat.hour >= startHour || stat.hour < endHour : stat.hour >= startHour && stat.hour < endHour
                    );
                }
            }),
            tap((occupancyData: Occupancy) => occupancyData.occupancyStats.filter((element) => element.day !== 0)),
            tap((occupancyData: Occupancy) => {
                occupancyData.occupancyStats.sort(
                    (a, b) =>
                        new Date(a.year, a.month - 1, a.day, a.hour).valueOf() - new Date(b.year, b.month - 1, b.day, b.hour).valueOf()
                );
            }),
            catchError((error) => {
                return this.handleError(error);
            }),
            share()
        );
    }
}
